'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { jwtDecode } from 'jwt-decode'

import { JWT_COOKIE_NAME, getCookie } from '@dc-shared-web/lib'

const REFRESH_THRESHOLD = 1000 * 60 * 10 // 10 minutes

type APITokenContextType = {
  token?: string
}

const APITokenContext = createContext<APITokenContextType>({
  token:
    typeof window !== 'undefined' && getCookie
      ? getCookie(JWT_COOKIE_NAME)
      : undefined,
})

const getTokenExpiration = (token: string | undefined) => {
  if (!token) {
    return undefined
  }

  try {
    const payload = jwtDecode(token)
    if (!payload || !payload.exp) {
      return undefined
    }
    return payload.exp * 1000
  } catch (e) {
    console.error('Failed to decode JWT', e)
    return undefined
  }
}

const getNewToken = async (publicURL: string): Promise<string | undefined> => {
  try {
    // Attempt to refresh the token using the session cookie
    const response = await fetch(`${publicURL}/ajax/login/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      redirect: 'error',
      credentials: 'include',
    })
    const jsonResponse = await response.json()
    return jsonResponse?.data?.token
  } catch (e) {
    console.warn('Failed attempting to refresh token', e)
  }
}

const redirectToTargetedLogin = (publicURL: string) => {
  /**
   * If a user clicks a link while unathenticated, provide necessary
   * information to redirect them back to the page they were trying to access.
   * Not adding this to the Axios 401 interceptor because we want those
   * instances to be truly logged out.
   */
  const encodedTarget = encodeURIComponent(window.location.href)
  window.location.href = `${publicURL}/login/?dcwTarget=${encodedTarget}`
}

export const APITokenProvider = ({
  publicURL,
  children,
}: {
  publicURL: string | undefined
  children: React.ReactNode
}) => {
  const [token, setToken] = useState<string | undefined>(
    typeof window !== 'undefined' ? getCookie(JWT_COOKIE_NAME) : undefined
  )
  const currentRefreshTimeout = useRef<NodeJS.Timeout | undefined>(undefined)

  const refreshToken = useCallback(async () => {
    const newToken = await getNewToken(publicURL!)
    if (!newToken) {
      // If we couldn't refresh, redirect to login
      redirectToTargetedLogin(publicURL!)
      return
    }
    setToken(newToken)
    return newToken
  }, [publicURL])

  const scheduleTokenRefresh = useCallback(
    async (token: string | undefined) => {
      if (currentRefreshTimeout.current) {
        clearTimeout(currentRefreshTimeout.current)
        currentRefreshTimeout.current = undefined
      }

      const tokenExpiration = getTokenExpiration(token)
      const nextRefresh = (tokenExpiration ?? 0) - REFRESH_THRESHOLD

      // If there is no token (-time) or the token expires soon, refresh immediately
      if (nextRefresh <= Date.now()) {
        const newToken = await refreshToken()
        if (newToken) {
          scheduleTokenRefresh(newToken)
        }
      } else {
        // Otherwise, schedule the token refresh for the next refresh threshold
        const timeUntilNextRefresh = nextRefresh - Date.now()
        currentRefreshTimeout.current = setTimeout(async () => {
          const newToken = await refreshToken()
          if (newToken) {
            scheduleTokenRefresh(newToken)
          }
        }, timeUntilNextRefresh)
      }
    },
    [refreshToken]
  )

  useEffect(() => {
    const token = getCookie(JWT_COOKIE_NAME)

    setToken(token)
    scheduleTokenRefresh(token)

    return () => {
      if (currentRefreshTimeout.current) {
        clearTimeout(currentRefreshTimeout.current)
      }
    }
  }, [scheduleTokenRefresh])

  return (
    <APITokenContext.Provider value={{ token }}>
      {children}
    </APITokenContext.Provider>
  )
}

export const useAPIToken = () => {
  return useContext(APITokenContext)
}
